import { RADashboardCardId, RADashboardCardSize } from "@/constants/ReportDashboard/cards-enums"
import { getSmallestCardSize, lookupCardSize } from "@/constants/ReportDashboard/cards-sizes"
import { CardSettings, RADashboardLayout } from "@/interfaces/raDashboard.interface"
import type { Layout, LayoutItem } from "react-grid-layout"

export const GUI_CANVAS_COLS = 12

/**
 * Converts a dashboard cardsOrder array into a react-grid-layout Layout.
 * Cards are packed left-to-right, top-to-bottom. Dense "bento box" style.
 */
export function cardsOrderToGridLayout(
  cards: (RADashboardCardId | string)[],
  cardSettings?: Record<string, CardSettings>
): Layout {
  const occupied = new Set<string>()
  const cell = (x: number, y: number) => `${x},${y}`

  const canPlace = (x: number, y: number, width: number, height: number): boolean => {
    if (x + width > GUI_CANVAS_COLS) return false
    for (let deltaY = 0; deltaY < height; deltaY++)
      for (let deltaX = 0; deltaX < width; deltaX++)
        if (occupied.has(cell(x + deltaX, y + deltaY))) return false
    return true
  }

  const markOccupied = (x: number, y: number, width: number, height: number) => {
    for (let deltaY = 0; deltaY < height; deltaY++)
      for (let deltaX = 0; deltaX < width; deltaX++)
        occupied.add(cell(x + deltaX, y + deltaY))
  }

  const findPosition = (width: number, height: number): { x: number; y: number } => {
    for (let y = 0; ; y++)
      for (let x = 0; x <= GUI_CANVAS_COLS - width; x++)
        if (canPlace(x, y, width, height)) return { x, y }
  }

  return cards.map(cardId => {
    const savedSize = (cardSettings?.[cardId]?.size ?? RADashboardCardSize.Medium) as RADashboardCardSize
    const size = lookupCardSize(cardId as RADashboardCardId, savedSize) ?? { width: 3, height: 2 }
    const { width, height } = size
    const { x, y } = findPosition(width, height)
    markOccupied(x, y, width, height)
    // Per-card resize floor — react-grid-layout's minMaxSize constraint reads
    // minW/minH off the item to clamp the bottom-right resize handle.
    const smallest = getSmallestCardSize(cardId)
    return {
      i: cardId,
      x,
      y,
      w: width,
      h: height,
      ...(smallest && { minW: smallest.width, minH: smallest.height }),
    }
  })
}

/**
 * Converts a react-grid-layout Layout back into a dashboard cardsOrder array.
 * All gaps are filled with `empty_WxH` spacer card keys so that when
 * cardsOrderToGridLayout re-processes the list it reproduces the exact same
 * visual positions — including layouts with multi-row cards next to shorter ones.
 *
 * Strategy: build a 2D occupancy grid, scan cells in reading order (y then x),
 * and emit each card when its top-left corner is reached. Every run of
 * contiguous empty cells becomes a single `empty_WxH` spacer sized to the
 * largest rectangle that fits without overlapping any real card.
 */
export function gridLayoutToCardsOrder(layout: Layout): (RADashboardCardId | string)[] {
  if (layout.length === 0) return []

  const maxRow = Math.max(...layout.map(item => item.y + item.h))

  // grid[y][x] = card ID that owns that cell, or null if empty
  const grid: (string | null)[][] = Array.from({ length: maxRow }, () =>
    new Array(GUI_CANVAS_COLS).fill(null)
  )
  for (const item of layout)
    for (let deltaY = 0; deltaY < item.h; deltaY++)
      for (let deltaX = 0; deltaX < item.w; deltaX++)
        grid[item.y + deltaY][item.x + deltaX] = item.i

  const processed: boolean[][] = Array.from({ length: maxRow }, () =>
    new Array(GUI_CANVAS_COLS).fill(false)
  )
  const result: (RADashboardCardId | string)[] = []

  for (let y = 0; y < maxRow; y++) {
    for (let x = 0; x < GUI_CANVAS_COLS; x++) {
      if (processed[y][x]) continue

      const cardId = grid[y][x]

      if (cardId !== null) {
        // Emit the card when we hit its top-left corner (reading order guarantees
        // we always reach top-left before any interior cell of the same card)
        const item = layout.find(it => it.i === cardId)!
        result.push(cardId)
        for (let deltaY = 0; deltaY < item.h; deltaY++)
          for (let deltaX = 0; deltaX < item.w; deltaX++)
            processed[y + deltaY][x + deltaX] = true
      } else {
        // Find max width of contiguous empty cells in this row
        let spacerWidth = 0
        while (x + spacerWidth < GUI_CANVAS_COLS && !processed[y][x + spacerWidth] && grid[y][x + spacerWidth] === null) spacerWidth++

        // Extend height while the same columns are fully empty in each subsequent row
        let spacerHeight = 1
        heightLoop: while (y + spacerHeight < maxRow) {
          for (let deltaX = 0; deltaX < spacerWidth; deltaX++) {
            if (grid[y + spacerHeight][x + deltaX] !== null || processed[y + spacerHeight][x + deltaX]) break heightLoop
          }
          spacerHeight++
        }

        result.push(`empty_${spacerWidth}x${spacerHeight}`)
        for (let deltaY = 0; deltaY < spacerHeight; deltaY++)
          for (let deltaX = 0; deltaX < spacerWidth; deltaX++)
            processed[y + deltaY][x + deltaX] = true
      }
    }
  }

  return result
}

/**
 * True if any item's position or size differs between the two layouts (or an
 * item from newLayout isn't present in currentLayout at all).
 */
export function hasLayoutItemMoved(currentLayout: Layout, newLayout: Layout): boolean {
  return newLayout.some(newItem => {
    const currentItem = currentLayout.find(item => item.i === newItem.i)
    return !currentItem
      || currentItem.x !== newItem.x
      || currentItem.y !== newItem.y
      || currentItem.w !== newItem.w
      || currentItem.h !== newItem.h
  })
}

/**
 * Returns a copy of `layout` with the given tab's cardsOrder replaced by the
 * positions in `newLayout` (drag moves, card removal, etc — anything that
 * doesn't change a card's stored size).
 */
export function applyCardsOrderChange(
  layout: RADashboardLayout,
  selectedTab: string,
  newLayout: Layout
): RADashboardLayout {
  return {
    ...layout,
    tabLayouts: {
      ...layout.tabLayouts,
      [selectedTab]: {
        ...layout.tabLayouts[selectedTab],
        cardsOrder: gridLayoutToCardsOrder(newLayout),
      },
    },
  }
}

/**
 * Returns a copy of `layout` with the resized card's footprint persisted as a
 * custom `custom_WxH` size, plus the tab's cardsOrder updated to match.
 */
export function applyResizeChange(
  layout: RADashboardLayout,
  selectedTab: string,
  newLayout: Layout,
  resizedItem: LayoutItem
): RADashboardLayout {
  return {
    ...applyCardsOrderChange(layout, selectedTab, newLayout),
    cardSettings: {
      ...layout.cardSettings,
      [resizedItem.i]: {
        ...layout.cardSettings?.[resizedItem.i],
        size: `${RADashboardCardSize.Custom}${resizedItem.w}x${resizedItem.h}`,
      },
    },
  }
}

/**
 * Drops the given card from a react-grid-layout Layout.
 */
export function removeCardFromGridLayout(gridLayout: Layout, cardId: string): Layout {
  return gridLayout.filter(item => item.i !== cardId)
}
